import { HttpClient, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Subscription } from 'rxjs';

/**
 * Глобальное состояние связи с API.
 *
 * Сетевой error-UX (зеркало мини-аппа): network-resilience-интерсептор после
 * исчерпания ретраев на сетевой ошибке (status 0 / ProgressEvent / TimeoutError)
 * вызывает `setOffline(req)` → выставляется `offline = true` и запоминается
 * ИСХОДНЫЙ запрос, поднявший оверлей. Показывается полноэкранный
 * `<app-connection-error>`. Любой последующий УСПЕШНЫЙ ответ снимает оверлей
 * (`setOnline()` → `offline = false`).
 *
 * Кнопка «Повторить» (`retry()`) ПЕРЕВЫПУСКАЕТ последний упавший запрос через
 * `HttpClient.request()` — он снова проходит всю цепочку интерсепторов, т.е.
 * получает свежие ретраи/таймаут. На успехе оверлей снимается; на ошибке
 * остаётся (интерсептор снова выставит offline). «Перезагрузить» —
 * `window.location.reload()` (жёсткий фолбэк).
 *
 * Граница ответственности: перевыпуск восстанавливает ОВЕРЛЕЙ при реальном
 * возврате связи, но НЕ перезапускает подписку исходного компонента (тот уже
 * получил ошибку). Это свойственно interceptor-модели: интерсептор видит
 * только HTTP-уровень и не знает о компонентах. Компонентный refetch-сигнал
 * (`retry$`, на который подписывались бы data-сервисы) сознательно НЕ
 * добавляется — ранее такой мёртвый `retry$` был удалён за ненадобностью, и
 * без реальных подписчиков он лишь вернул бы тот же мусор. Если когда-нибудь
 * понадобится сквозной refetch — это отдельная, осознанно разводимая фича.
 */
@Injectable({
  providedIn: 'root'
})
export class ConnectionStateService {
  private readonly http = inject(HttpClient);

  /** true — связь с сервером потеряна (исчерпаны ретраи сетевой ошибки). */
  readonly offline = signal(false);

  /**
   * Запрос, поднявший оверлей (последний упавший с сетевой ошибкой).
   * Используется «Повторить» для перевыпуска. null — перевыпускать нечего.
   */
  private lastFailedRequest: HttpRequest<unknown> | null = null;

  /**
   * Подписка идущего сейчас перевыпуска. Не null => перевыпуск «в полёте»:
   * повторные клики «Повторить» игнорируются (single-flight), а сама ссылка
   * нужна, чтобы не утекла подписка.
   */
  private retrySub: Subscription | null = null;

  /**
   * Перевести в offline (вызывается интерсептором при исчерпании ретраев).
   * Запоминает упавший запрос, чтобы «Повторить» мог его перевыпустить.
   */
  setOffline(request?: HttpRequest<unknown>): void {
    if (request) {
      this.lastFailedRequest = request;
    }
    this.offline.set(true);
  }

  /** Снять offline (вызывается интерсептором на первом успешном ответе). */
  setOnline(): void {
    if (this.offline()) {
      this.offline.set(false);
    }
    // Связь восстановлена сама по себе — запомненный запрос больше не актуален.
    this.lastFailedRequest = null;
  }

  /**
   * «Повторить»: перевыпустить последний упавший запрос через всю цепочку
   * интерсепторов (свежие ретраи/таймаут).
   *  - нет запомненного запроса → просто скрыть оверлей (чистый dismiss);
   *  - перевыпуск уже идёт → игнорировать клик (single-flight);
   *  - успех → снять оверлей и очистить запомненный запрос;
   *  - ошибка → оверлей оставить (интерсептор снова выставит offline).
   */
  retry(): void {
    if (this.retrySub) {
      // Перевыпуск уже в полёте — не плодим параллельные запросы.
      return;
    }

    const request = this.lastFailedRequest;
    if (!request) {
      // Перевыпускать нечего — деградируем до чистого dismiss.
      this.offline.set(false);
      return;
    }

    // request(HttpRequest) отдаёт ПОЛНЫЙ поток событий (Sent, прогресс, …),
    // поэтому успехом считаем ТОЛЬКО финальный HttpResponse — иначе ранний
    // Sent-event ложно «снял» бы оверлей. Это зеркалит tap в интерсепторе.
    this.retrySub = this.http.request(request).subscribe({
      next: (event) => {
        if (event instanceof HttpResponse) {
          // Успех ловит и tap интерсептора (setOnline), но снимаем оверлей и
          // здесь — явно и независимо от того, кто отработал первым.
          this.offline.set(false);
          this.lastFailedRequest = null;
          this.clearRetry();
        }
      },
      error: () => {
        // Оверлей не трогаем: интерсептор уже снова выставил offline.
        // Запрос сохраняем — пользователь сможет повторить ещё раз.
        this.clearRetry();
      },
    });
  }

  /** Снять single-flight-замок и отписаться от завершённого перевыпуска. */
  private clearRetry(): void {
    this.retrySub?.unsubscribe();
    this.retrySub = null;
  }
}
